import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { usePageMeta } from '@/hooks/usePageMeta';
import { stats, testimonials, aboutContent } from '@/mocks/manhwa';

export default function AboutPage() {
  usePageMeta({
    title: '关于我们_虚空韩漫网 - 韩漫领军者',
    description: '虚空韩漫网成立于2022年，是国内领先的韩国漫画在线阅读平台。收录超15,800部韩漫作品，服务超过286万用户。了解我们的品牌故事、发展历程和联系方式。',
    keywords: '虚空韩漫网,关于我们,韩漫平台,漫画网站,韩漫阅读',
    canonicalUrl: 'http://jiukuguoji.com/about',
    ogType: 'website',
  });

  return (
    <>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative h-[380px] md:h-[500px] w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20golden%20amber%20and%20soft%20cream%20abstract%20gradient%20illustration%20with%20elegant%20flowing%20curves%2C%20brand%20atmosphere%2C%20refined%20minimalist%20composition%2C%20digital%20art%2C%20warm%20inviting%20feel%2C%20no%20text&width=1920&height=1000&seq=about-hero-bg&orientation=landscape"
            alt="关于虚空韩漫网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-4 text-center">
            <h1 className="text-2xl md:text-5xl font-heading font-black text-white tracking-wide mb-3">
              关于虚空 · 韩漫领军者
            </h1>
            <p className="text-sm md:text-lg text-white/80 max-w-2xl leading-relaxed mb-6">
              连接读者与优质韩漫，无缝相遇，随心阅读<br />
              致力于打造中文世界最好的韩漫阅读社区
            </p>
            <a href="#brand-story" className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-white font-medium text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
              了解我们
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-line"></i></span>
            </a>
          </div>
        </section>

        <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />

        {/* Brand Story */}
        <section id="brand-story" className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-18">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4">
              品牌故事
            </span>
            <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              让每一位读者都能找到心仪的作品
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {aboutContent.brandIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-start mt-12">
            <div className="md:col-span-3">
              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{aboutContent.mission}</p>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutContent.vision}</p>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">联系方式</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">邮箱</p>
                      <p className="text-sm text-foreground-800 font-medium">{aboutContent.contact.email}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                      <i className="ri-phone-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">电话</p>
                      <p className="text-sm text-foreground-800 font-medium">{aboutContent.contact.phone}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">地址</p>
                      <p className="text-sm text-foreground-800 font-medium">{aboutContent.contact.address}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Stats */}
        <section className="w-full bg-background-100 py-12 md:py-18">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <h3 className="text-lg md:text-2xl font-bold text-foreground-900 leading-snug mb-8">
                  连接读者<br />与优质韩漫<br />无缝相遇
                </h3>
                <div className="grid grid-cols-3 divide-x divide-background-300">
                  <div className="pr-3 md:pr-5">
                    <div className="text-xl md:text-3xl font-heading font-bold text-accent-500">{(stats.totalUsers / 10000).toFixed(0)}万+</div>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">平台总用户</p>
                  </div>
                  <div className="px-3 md:px-5">
                    <div className="text-xl md:text-3xl font-heading font-bold text-accent-500">{stats.totalWorks.toLocaleString()}+</div>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">全站漫画量</p>
                  </div>
                  <div className="pl-3 md:pl-5">
                    <div className="text-xl md:text-3xl font-heading font-bold text-accent-500">{stats.dailyVisits.toLocaleString()}+</div>
                    <p className="text-xs md:text-sm text-foreground-500 mt-1">日均访问量</p>
                  </div>
                </div>
              </div>
              <div className="lg:w-1/2">
                <div className="relative min-h-[220px] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Soft%20warm%20pink%20to%20peach%20abstract%20shapes%20with%20overlapping%20organic%20ovals%20and%20flowing%20gradients%2C%20brand%20identity%20digital%20art%2C%20gentle%20layering%2C%20modern%20minimalist&width=640&height=440&seq=about-stats-decor&orientation=landscape"
                    alt=""
                    className="w-full h-full object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-12 md:py-18">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4">
              发展历程
            </span>
            <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">我们的成长之路</h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <div className="absolute left-3 md:left-4 top-2 bottom-2 w-0.5 bg-accent-200"></div>
              {aboutContent.milestones.map((item, idx) => (
                <div key={idx} className="relative pl-10 md:pl-12 pb-8 last:pb-0">
                  <div className="absolute left-1.5 md:left-2.5 top-1 w-3.5 h-3.5 rounded-full bg-accent-500 border-2 border-background-50 z-10"></div>
                  <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
                    <span className="text-xs font-bold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">{item.year}</span>
                    <p className="text-sm text-foreground-700 mt-2 leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full bg-background-100 py-12 md:py-18">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3">
                口碑见证
              </span>
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-900">
                数百万读者的共同信赖
              </h2>
            </div>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5">
              {testimonials.map((item) => (
                <div key={item.id} className="break-inside-avoid mb-4 md:mb-5 bg-white rounded-xl border border-background-200 p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 md:w-11 md:h-11 rounded-full overflow-hidden flex-shrink-0">
                      <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{item.name}</div>
                      <div className="text-xs text-foreground-500">@{item.name}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    &ldquo;{item.comment}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20golden%20amber%20and%20peach%20soft%20gradient%20abstract%20art%20with%20flowing%20organic%20shapes%2C%20inviting%20warm%20atmosphere%2C%20digital%20minimalist%20composition%2C%20clean%20elegant%20background%2C%20no%20text&width=1920&height=800&seq=about-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
          <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-4xl font-heading font-black text-white tracking-wide mb-3 md:mb-4">
              加入虚空韩漫大家庭
            </h2>
            <p className="text-sm md:text-lg text-white/80 mb-7 md:mb-9 max-w-xl mx-auto leading-relaxed">
              数百万韩漫爱好者已经加入，现在就开启你的韩漫阅读之旅
            </p>
            <Link
              to="/category/romance"
              className="group inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-medium text-sm rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-book-open-line"></i></span>
              免费开始阅读
              <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform"><i className="ri-arrow-right-up-line"></i></span>
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      <SchemaOrg type="WebPage" data={{
        url: 'http://jiukuguoji.com/about',
        name: '关于我们 - 虚空韩漫网',
        description: '虚空韩漫网成立于2022年，国内领先的韩国漫画在线阅读平台。',
        isPartOf: { '@type': 'WebSite', name: '虚空韩漫网', url: 'http://jiukuguoji.com/' },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://jiukuguoji.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }} />
    </>
  );
}